import { useState } from "react";
import type { CognitoUser } from "amazon-cognito-identity-js";

import type { AppConfig } from "../lib/config";
import {
  completeNewPassword,
  entraConfigured,
  NewPasswordRequired,
  signIn,
  startEntraSignIn,
} from "../lib/auth";
import type { Identity } from "../lib/auth";

interface Props {
  config: AppConfig;
  onSignedIn: (identity: Identity) => void;
}

export default function SignIn({ config, onSignedIn }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  // Cognito forces a password change on first login for admin-created accounts,
  // which is how the demo users are provisioned — so this is the normal path.
  const [challengeUser, setChallengeUser] = useState<CognitoUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (challengeUser) {
        onSignedIn(await completeNewPassword(challengeUser, email, newPassword));
      } else {
        onSignedIn(await signIn(config, email, password));
      }
    } catch (err) {
      if (err instanceof NewPasswordRequired) {
        setChallengeUser(err.user);
        setError("This account needs a new password before first use.");
      } else {
        setError(err instanceof Error ? err.message : "Sign-in failed.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4"
      >
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Student Management System</h1>
          <p className="text-sm text-slate-500 mt-1">
            Sign in with your institutional account.
          </p>
        </div>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Email</span>
          <input
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
          />
        </label>

        {challengeUser ? (
          <label className="block">
            <span className="text-sm font-medium text-slate-700">New password</span>
            <input
              type="password"
              required
              minLength={12}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
            />
            <span className="text-xs text-slate-500 mt-1 block">
              At least 12 characters, with upper case, lower case and a digit.
            </span>
          </label>
        ) : (
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
            />
          </label>
        )}

        {error && (
          <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-md bg-slate-900 text-white text-sm font-medium py-2 hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? "Signing in…" : challengeUser ? "Set password and sign in" : "Sign in"}
        </button>

        {/* Staff sign in through Entra; the redirect resolves their identity and
            entitlement server-side. This is the intended path for staff — the
            username/password form above is a native-account fallback. */}
        {!challengeUser && entraConfigured(config) && (
          <>
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <span className="h-px flex-1 bg-slate-200" />
              or
              <span className="h-px flex-1 bg-slate-200" />
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setError(null);
                setBusy(true);
                startEntraSignIn(config).catch((err) => {
                  setError(err instanceof Error ? err.message : "Could not start staff sign-in.");
                  setBusy(false);
                });
              }}
              className="w-full rounded-md border border-slate-300 bg-white text-slate-800 text-sm font-medium py-2 hover:bg-slate-50 disabled:opacity-50"
            >
              Staff sign-in with Microsoft Entra
            </button>
          </>
        )}
      </form>
    </div>
  );
}
